
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";

import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// =====================================
// NEWS TAG COLORS
// =====================================

const TAG_COLORS = [
  "#00F0FF",
  "#0055FF",
  "#7C3AED",
  "#00F0FF",
];

// =====================================
// ARTICLE TRANSLATION
// =====================================

function pickLang(item, language) {
  const tr = item.translations || {};

  return (
    tr[language] ||
    tr[item.source_lang] ||
    tr.en ||
    Object.values(tr)[0] ||
    {}
  );
}

// =====================================
// NEWS PAGE
// =====================================

export default function News() {
  const { t, language } = useLanguage();
  const news = t.news;

  const [items, setItems] = useState(null);

  // =====================================
  // LOAD NEWS FROM BACKEND
  // =====================================

  useEffect(() => {
    api.getNews()
      .then((data) => {
        console.log("NEWS API RESPONSE:", data);

        setItems(
          Array.isArray(data)
            ? data
            : []
        );
      })
      .catch((error) => {
        console.error("NEWS API ERROR:", error);

        setItems([]);
      });
  }, []);

  // =====================================
  // SEO METADATA
  // =====================================

  useEffect(() => {
    document.title =
      "News & Updates | FrontSpace";

    const description =
      "Read the latest cybersecurity news, artificial intelligence developments, technology insights, and digital security updates from FrontSpace.";

    let meta = document.querySelector(
      'meta[name="description"]'
    );

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", description);
  }, []);

  return (
    <div
      className="cyber-bg cyber-grid-bg min-h-screen"
      style={{
        backgroundColor: "#050814",
      }}
    >
      {/* ===================================== */}
      {/* BACKGROUND GLOW */}
      {/* ===================================== */}

      <div
        className="glow-orb w-[400px] h-[400px] top-40 left-0 opacity-10"
        style={{
          background:
            "radial-gradient(circle, #0055FF 0%, transparent 70%)",
        }}
      />

      {/* ===================================== */}
      {/* NAVBAR */}
      {/* ===================================== */}

      <Navbar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* ===================================== */}
        {/* SIMPLE NEWS HEADER */}
        {/* ===================================== */}

        <div className="pt-32 pb-10 text-center">
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.5,
            }}
          >
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{
                fontFamily: "Outfit, sans-serif",
              }}
            >
              News{" "}
              <span className="text-[#00F0FF]">
                & Updates
              </span>
            </h1>
          </motion.div>
        </div>

        {/* ===================================== */}
        {/* LOADING STATE */}
        {/* ===================================== */}

        {items === null && (
          <div className="flex justify-center py-20 text-[#00F0FF]">
            <Loader2
              className="animate-spin"
              size={28}
            />
          </div>
        )}

        {/* ===================================== */}
        {/* EMPTY NEWS STATE */}
        {/* ===================================== */}

        {items !== null &&
          items.length === 0 && (
            <p className="text-center text-slate-500 py-20">
              {news.empty}
            </p>
          )}

        {/* ===================================== */}
        {/* NEWS ARTICLES */}
        {/* ===================================== */}

        {items !== null &&
          items.length > 0 && (
            <div className="relative mb-24">

              {/* ===================================== */}
              {/* TIMELINE LINE */}
              {/* ===================================== */}

              <div
                className="absolute left-4 sm:left-8 top-0 bottom-0 w-px"
                style={{
                  background:
                    "linear-gradient(to bottom, transparent, #00F0FF30, transparent)",
                }}
              />

              <div className="space-y-8">

                {items.map((item, i) => {
                  const c = pickLang(
                    item,
                    language
                  );

                  const tagColor =
                    TAG_COLORS[
                      i % TAG_COLORS.length
                    ];

                  // Articles must use backend slugs.
                  // Do not substitute database IDs.

                  const articleSlug = item.slug;

                  return (
                    <motion.div
                      key={
                        item.id ||
                        item.slug ||
                        i
                      }
                      initial={{
                        opacity: 0,
                        x: 30,
                      }}
                      whileInView={{
                        opacity: 1,
                        x: 0,
                      }}
                      viewport={{
                        once: true,
                      }}
                      transition={{
                        delay: Math.min(
                          i * 0.08,
                          0.4
                        ),
                      }}
                      className="relative pl-12 sm:pl-20"
                      data-testid={`news-item-${i}`}
                    >

                      {/* ===================================== */}
                      {/* TIMELINE DOT */}
                      {/* ===================================== */}

                      <div
                        className="absolute left-2 sm:left-6 top-5 w-4 h-4 rounded-full border-2 flex items-center justify-center"
                        style={{
                          borderColor: tagColor,
                          background:
                            `${tagColor}20`,
                        }}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{
                            background: tagColor,
                          }}
                        />
                      </div>

                      {/* ===================================== */}
                      {/* ARTICLE CARD */}
                      {/* ===================================== */}

                      <article className="cyber-card p-6 sm:p-8 group">

                        {/* ARTICLE DATE AND TAG */}

                        <div className="flex flex-wrap items-center gap-3 mb-4">

                          <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                            <Calendar size={12} />

                            <span className="terminal">
                              {item.date}
                            </span>
                          </div>

                          {c.tag && (
                            <span
                              className="text-xs px-2 py-0.5 rounded terminal"
                              style={{
                                color: tagColor,
                                background:
                                  `${tagColor}15`,
                                border:
                                  `1px solid ${tagColor}30`,
                              }}
                            >
                              {c.tag}
                            </span>
                          )}

                        </div>

                        {/* ARTICLE TITLE */}

                        <h2
                          className="text-xl font-bold text-white mb-3 group-hover:text-[#00F0FF] transition-colors"
                          style={{
                            fontFamily:
                              "Outfit, sans-serif",
                          }}
                        >
                          {c.title}
                        </h2>

                        {/* ARTICLE SUMMARY */}

                        {c.summary && (
                          <p className="text-slate-400 text-sm leading-relaxed mb-5">
                            {c.summary}
                          </p>
                        )}

                        {/* ===================================== */}
                        {/* READ ARTICLE LINK */}
                        {/* ===================================== */}

                        {articleSlug ? (
                          <Link
                            to={`/news/${encodeURIComponent(
                              articleSlug
                            )}`}
                            className="inline-flex items-center gap-2 text-[#00F0FF] text-xs terminal hover:gap-3 transition-all"
                          >
                            {news.readMore}

                            <ArrowRight
                              size={12}
                            />
                          </Link>
                        ) : (
                          <span className="text-slate-600 text-xs terminal">
                            Article unavailable
                          </span>
                        )}

                      </article>

                    </motion.div>
                  );
                })}

              </div>
            </div>
          )}

      </main>

      {/* ===================================== */}
      {/* FOOTER */}
      {/* ===================================== */}

      <Footer />

    </div>
  );
}
